import React, { useEffect, useState, useRef } from "react";
import "./ParameterGraph.scss";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { getNX5PatientHistoryGraphData, getNX5PatientHistoryCursorData } from "../../../helper/patient-history-helper";
import { idToKey } from "../../../common/utils/NX5IdMap/MonitorParametersIdMap";
import { nx5PatientHistoryCursorData, nx5PatientHistoryGraphApiData, nx5PatientHistoryCurrentCursorTime } from "../../../modules/actions/action";
import { connect } from "react-redux";
import Draggable from "react-draggable";

import {
    Chart,
    LineElement,
    PointElement,
    LineController,
    CategoryScale,
    LinearScale,
    TimeScale,
    Tooltip,
    Legend,
} from "chart.js";

import "chartjs-adapter-moment";

Chart.register(
    LineElement,
    PointElement,
    LineController,
    CategoryScale,
    LinearScale,
    TimeScale,
    Tooltip,
    Legend
);

const BATCH_SIZE = 4;
/** Above this window length we use bucketed cursor API data for the scrubber; at or below we use raw points. */
const CURSOR_BUCKET_THRESHOLD_MINUTES = 15;
const RAW_TITLE_SNAP_MS = 2000;

function hasPatientHistoryTimeData(timeData) {
    return Boolean(timeData && typeof timeData === "object" && Object.keys(timeData).length > 0);
}

function rangeDurationMinutes(startDateTime, endDateTime) {
    const start = new Date(startDateTime).getTime();
    const end = new Date(endDateTime).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return 0;
    return (end - start) / 60000;
}

/** Timestamp ms for a graph row (Chart `{x,y}` or API `{ observed_at, value }`). */
function getHistoryPointTimeMs(point) {
    if (!point) return null;
    const raw = point.x ?? point.observed_at;
    if (raw == null) return null;
    const ms = new Date(raw).getTime();
    return Number.isNaN(ms) ? null : ms;
}

function getHistoryPointValue(point) {
    if (!point) return null;
    const v = point.y ?? point.value;
    return typeof v === "number" ? v : null;
}

function formatParamTitle(paramId) {
    const key = idToKey(paramId);
    return (typeof key === "string" ? key.toUpperCase() : String(paramId));
}

/** Chart.js time/linear line charts expect `{ x, y }`. NX5 history API returns `{ value, observed_at }`. */
function mapPatientHistoryRowsToChartPoints(rows) {
    if (!Array.isArray(rows) || !rows.length) return [];
    return rows
        .map((p) => {
            if (p == null) return null;
            if (typeof p.y === "number" && p.x != null) {
                return { x: p.x, y: p.y };
            }
            if (typeof p.value === "number" && p.observed_at != null) {
                return { x: p.observed_at, y: p.value };
            }
            return null;
        })
        .filter(Boolean);
}

function ParameterGraph(props) {
    const [graphValues, setGraphValues] = useState({});
    const [batchIndex, setBatchIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const timeRef = useRef(null);
    const [cursorData, setCursorData] = useState({});
    const sliderRef = useRef(null);
    const draggableRef = useRef(null);
    const graphRefs = useRef({});
    const cursorLineRef = useRef(null);
    const graphTitleRefs = useRef({});
    const [bounds, setBounds] = useState({});
    const lastDragTimeRefCursorLine = useRef(0);
    const draggableRefCursorLine = useRef(null);

    const loadNextBatch = async (isReset = false) => {
        if (loading) return;
        setLoading(true);

        const start = isReset ? 0 : batchIndex * BATCH_SIZE;
        const end = start + BATCH_SIZE;
        const batchIds = props?.selectedParamList?.slice(start, end) || [];

        if (batchIds.length === 0) {
            setHasMore(false);
            setLoading(false);
            return;
        }

        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData)) {
            setLoading(false);
            return;
        }

        const timeData = props.PatientHistoryTimeData;
        const graphPayload = props.PatientHistoryGraphData;
        const hasCache =
            graphPayload &&
            !graphPayload.isLoading &&
            graphPayload.data &&
            typeof graphPayload.data === "object";

        const mergeIntoGraphState = (patch) => {
            setGraphValues((prev) => ({ ...prev, ...patch }));
        };

        try {
            if (hasCache) {
                const cacheGraphAPIData = graphPayload.data;
                const cacheKeys = Object.keys(cacheGraphAPIData);
                const presentIdsData = {};
                const missingFromCache = [];

                batchIds.forEach((id) => {
                    const key = String(id);
                    if (cacheKeys.includes(key)) {
                        presentIdsData[key] = cacheGraphAPIData[key];
                    } else {
                        missingFromCache.push(id);
                    }
                });

                if (Object.keys(presentIdsData).length > 0) {
                    mergeIntoGraphState(presentIdsData);
                }

                if (missingFromCache.length > 0) {
                    props.nx5PatientHistoryGraphApiData({
                        bedId: props.bedId,
                        isLoading: true,
                        data: cacheGraphAPIData,
                    });

                    const res = await getNX5PatientHistoryGraphData({
                        bedId: props.bedId,
                        startDateTime: timeData.startDateTime,
                        endDateTime: timeData.endDateTime,
                        selectedParamList: missingFromCache,
                    });

                    mergeIntoGraphState(res.values || {});
                    props.nx5PatientHistoryGraphApiData({
                        bedId: props.bedId,
                        isLoading: false,
                        data: { ...cacheGraphAPIData, ...(res.values || {}) },
                    });
                }
            } else {
                props.nx5PatientHistoryGraphApiData({
                    bedId: props.bedId,
                    isLoading: true,
                    data: {},
                });

                const res = await getNX5PatientHistoryGraphData({
                    bedId: props.bedId,
                    startDateTime: timeData.startDateTime,
                    endDateTime: timeData.endDateTime,
                    selectedParamList: batchIds,
                });

                mergeIntoGraphState(res.values || {});
                props.nx5PatientHistoryGraphApiData({
                    bedId: props.bedId,
                    isLoading: false,
                    data: { ...(res.values || {}) },
                });
            }

            setBatchIndex((prev) => prev + 1);
            if (batchIds.length < BATCH_SIZE) {
                setHasMore(false);
            }
        } catch (err) {
            console.error("getNX5PatientHistoryGraphData failed:", err);
            props.nx5PatientHistoryGraphApiData({
                bedId: props.bedId,
                isLoading: false,
                data: props.PatientHistoryGraphData?.data || {},
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!props.selectedParamList.length) return;
        setGraphValues({});
        setBatchIndex(0);
        setHasMore(true);
        const container = document.querySelector(".patient-history-graphs-container");
        if (container) {
            container.scrollTop = 0;
        }
        loadNextBatch(true);
    }, [props.selectedParamList, props.PatientHistoryTimeData]);

    /** Infinite scroll: load the next graph batch near the bottom of the list. */
    useEffect(() => {
        const container = document.querySelector(".patient-history-graphs-container");
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, clientHeight, scrollHeight } = container;

            const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
            // 150px buffer before bottom

            if (isNearBottom && hasMore && !loading) {
                loadNextBatch();
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [hasMore, loading, props.PatientHistoryTimeData, props.selectedParamList, props.PatientHistoryGraphData, batchIndex]);



    const updateGraphTitlesFromCursorData = (timestamp) => {
        if (!timestamp || !cursorData || Object.keys(cursorData).length === 0) return;

        Object.keys(graphRefs.current).forEach((paramId) => {
            const el = graphTitleRefs.current?.[paramId];
            if (!el) return;

            const value = cursorData?.[timestamp]?.[paramId];
            const paramTitle = formatParamTitle(paramId);
            el.textContent = `${paramTitle} : ${value ?? "--"}`;
        });
    };

    const updateGraphTitlesFromRawData = (timestamp) => {
        if (!timestamp || !graphValues || Object.keys(graphValues).length === 0) return;
        const targetTime = new Date(timestamp).getTime();

        Object.keys(graphRefs.current).forEach((paramId) => {
            const el = graphTitleRefs.current[paramId];
            if (!el) return;

            const dataArr = graphValues[paramId];
            const title = formatParamTitle(paramId);
            if (!dataArr || dataArr.length === 0) {
                el.textContent = `${title} : --`;
                return;
            }

            let left = 0;
            let right = dataArr.length - 1;
            let idx = -1;

            while (left <= right) {
                const mid = (left + right) >> 1;
                const midTime = getHistoryPointTimeMs(dataArr[mid]);
                if (midTime == null) {
                    right = mid - 1;
                    continue;
                }
                if (midTime <= targetTime) {
                    idx = mid;
                    left = mid + 1;
                } else {
                    right = mid - 1;
                }
            }

            if (idx === -1) {
                el.textContent = `${title} : --`;
                return;
            }

            const pkt = dataArr[idx];
            const pointMs = getHistoryPointTimeMs(pkt);
            if (pointMs == null) {
                el.textContent = `${title} : --`;
                return;
            }

            const diff = targetTime - pointMs;
            const displayVal = getHistoryPointValue(pkt);
            if (diff <= RAW_TITLE_SNAP_MS && displayVal != null) {
                el.textContent = `${title} : ${displayVal}`;
            } else {
                el.textContent = `${title} : --`;
            }
        });
    };

    const getCurrentXTime = (x, startDateTime, endDateTime, useRaw) => {
        const newBounds = calculateNewBounds();
        if (!newBounds) return null;
        const { left, width } = newBounds;
        if (!startDateTime || !endDateTime || width <= 0) return null;

        const xInPlot = x - left;

        if (useRaw) {
            const spanMs = endDateTime - startDateTime;
            return moment
                .utc(startDateTime)
                .add((spanMs * xInPlot) / width, "ms")
                .toISOString();
        }

        if (!cursorData || Object.keys(cursorData).length === 0) return null;

        const keys = Object.keys(cursorData);
        if (keys.length === 0) return null;
        if (keys.length === 1) return keys[0];

        const bucketCount = keys.length - 1;
        const bucketWidth = width / bucketCount;
        let index = Math.round(xInPlot / bucketWidth);
        index = Math.max(0, Math.min(index, keys.length - 1));
        return keys[index];
    };

    const updateCursor = (x) => {
        if (cursorLineRef.current) {
            cursorLineRef.current.style.transform = `translate(${x}px, 0px)`;
        }

        if (draggableRefCursorLine.current) {
            draggableRefCursorLine.current.setState({ x, y: 0 });
        }

        if (sliderRef.current) {
            sliderRef.current.style.transform = `translate(${x}px, 0px)`;
        }

        if (draggableRef.current) {
            draggableRef.current.setState({ x, y: 0 });
        }

        const startDateTime = props.PatientHistoryTimeData?.startDateTime
            ? new Date(props.PatientHistoryTimeData.startDateTime)
            : null;
        const endDateTime = props.PatientHistoryTimeData?.endDateTime
            ? new Date(props.PatientHistoryTimeData.endDateTime)
            : null;
        if (!startDateTime || !endDateTime) return;

        const diffMinutes = rangeDurationMinutes(startDateTime, endDateTime);
        const useRaw = diffMinutes <= CURSOR_BUCKET_THRESHOLD_MINUTES;

        const nearestTime = getCurrentXTime(x, startDateTime, endDateTime, useRaw);
        if (!nearestTime) return;

        if (timeRef.current) {
            timeRef.current.textContent = moment.utc(nearestTime).utcOffset(330).format("HH:mm:ss");
            timeRef.current.utcTime = nearestTime;
        }

        if (useRaw) {
            updateGraphTitlesFromRawData(nearestTime);
        } else {
            updateGraphTitlesFromCursorData(nearestTime);
        }

        props.nx5PatientHistoryCurrentCursorTime({
            bedId: props.bedId,
            currentCursorTime: nearestTime,
        });
    };

    const waitForBounds = () => {
        return new Promise(resolve => {
            const check = () => {
                const b = calculateNewBounds();
                if (b) return resolve(b);
                requestAnimationFrame(check); // keep checking until ready
            };
            check();
        });
    };

    useEffect(() => {
        (async () => {
            const b = await waitForBounds();
            updateCursor(b.left);
        })();

    }, [cursorData]);

    const loadCursorData = async () => {
        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData)) return;

        const cursorPayload = props.PatientHistoryCursorData;
        const fromCache =
            cursorPayload &&
            typeof cursorPayload === "object" &&
            Object.keys(cursorPayload).length > 0 &&
            !cursorPayload.isLoading;

        if (fromCache) {
            setCursorData(cursorPayload.cursorData || {});
            return;
        }

        const timeData = props.PatientHistoryTimeData;
        const apiObj = {
            bedId: props.bedId,
            startDateTime: timeData.startDateTime,
            endDateTime: timeData.endDateTime,
            selectedParamList: props.selectedParamList,
        };

        const diffMinutes = rangeDurationMinutes(apiObj.startDateTime, apiObj.endDateTime);

        try {
            if (diffMinutes > CURSOR_BUCKET_THRESHOLD_MINUTES) {
                props.nx5PatientHistoryCursorData({
                    bedId: props.bedId,
                    isLoading: true,
                    cursorData: {},
                });
                const res = await getNX5PatientHistoryCursorData(apiObj);
                setCursorData(res.data || {});
                props.nx5PatientHistoryCursorData({
                    bedId: props.bedId,
                    isLoading: false,
                    cursorData: res.data || {},
                });
            } else {
                setCursorData({});
                props.nx5PatientHistoryCursorData({
                    bedId: props.bedId,
                    isLoading: false,
                    cursorData: {},
                });
            }
        } catch (err) {
            console.error("getNX5PatientHistoryCursorData failed:", err);
            props.nx5PatientHistoryCursorData({
                bedId: props.bedId,
                isLoading: false,
                cursorData: {},
            });
        }
    };

    useEffect(() => {
        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData)) return;
        loadCursorData();
    }, [props.PatientHistoryTimeData]);


    const calculateNewBounds = () => {
        const keys = Object.keys(graphRefs.current);
        if (keys.length === 0) {
            return null;
        }

        let chart = null;
        for (const k of keys) {
            if (graphRefs.current[k]) {
                chart = graphRefs.current[k];
                break;
            }
        }
        if (!chart) {
            return null
        };

        const canvas = chart.canvas;
        if (!canvas) {
            return null
        };

        // chart.chartArea gives the actual plotting area
        const chartArea = chart.chartArea;
        if (!chartArea) return null; // might not be ready yet

        const graphRect = canvas.getBoundingClientRect();
        const parentRect = canvas.parentElement.getBoundingClientRect();

        const left = graphRect.left - parentRect.left + chartArea.left;
        const width = chartArea.right - chartArea.left;
        const right = left + width;

        return { left, right, width };
    }

    const updateGraphBounds = () => {
        const newBounds = calculateNewBounds();
        if (!newBounds) return;

        const { left, right, width } = newBounds;

        // only update if changed
        setBounds(prev => {
            if (prev.left !== left || prev.right !== right) {
                return { left, right };
            }
            return prev;
        });
    };

    useEffect(() => {
        if (!graphValues || Object.keys(graphValues).length === 0) return;
        if (!timeRef.current || !timeRef.current.utcTime) return;

        const timeData = props.PatientHistoryTimeData;
        if (!hasPatientHistoryTimeData(timeData)) return;

        const diffMinutes = rangeDurationMinutes(timeData.startDateTime, timeData.endDateTime);
        const utcTime = timeRef.current.utcTime;
        if (diffMinutes > CURSOR_BUCKET_THRESHOLD_MINUTES) {
            updateGraphTitlesFromCursorData(utcTime);
        } else {
            updateGraphTitlesFromRawData(utcTime);
        }
    }, [graphValues]);

    const graphKeys = props.selectedParamList?.length
        ? props.selectedParamList.filter((id) => id in graphValues)
        : Object.keys(graphValues);
    const lastDragTimeRef = useRef(0);

    return (
        <>
            <div className="history-graphs">
                <Draggable
                    axis="x"
                    bounds={bounds}
                    nodeRef={cursorLineRef}
                    onDrag={(e, data) => {
                        const now = Date.now();

                        if (cursorLineRef.current) {
                            cursorLineRef.current.style.transform = `translate(${data.x}px, 0px)`;
                        }


                        if (sliderRef.current) {
                            sliderRef.current.style.transform = `translate(${data.x}px, 0px)`;
                        }
                        if (draggableRef.current) {
                            draggableRef.current.setState({ x: data.x, y: 0 });
                        }

                        if (now - lastDragTimeRefCursorLine.current >= 500) {
                            lastDragTimeRefCursorLine.current = now;
                            updateCursor(data.x);   // throttled updates
                        }
                    }}
                    onStop={(e, data) => {
                        updateCursor(data.x);       // FINAL accurate value
                    }}
                    ref={draggableRefCursorLine}
                >
                <div className="cursor-dotted-line" ref={(el) => { cursorLineRef.current = el }} />

                </Draggable>
                <div className="patient-history-cursor">
                    <Draggable
                        axis="x"
                        bounds={bounds}
                        nodeRef={sliderRef}
                        onDrag={(e, data) => {
                            const now = Date.now();

                            if (sliderRef.current) {
                                sliderRef.current.style.transform = `translate(${data.x}px, 0px)`;
                            }


                            if (cursorLineRef.current) {
                                cursorLineRef.current.style.transform = `translate(${data.x}px, 0px)`;
                            }
                            if (draggableRefCursorLine.current) {
                                draggableRefCursorLine.current.setState({ x: data.x, y: 0 });
                            }
                            if (now - lastDragTimeRef.current >= 500) {
                                lastDragTimeRef.current = now;
                                updateCursor(data.x);   // throttled updates
                            }
                        }}
                        onStop={(e, data) => {
                            updateCursor(data.x);       // FINAL accurate value
                        }}
                        ref={draggableRef}
                    >
                        <div className="cursor-slider" ref={(el) => { sliderRef.current = el; timeRef.current = el; }} />
                    </Draggable>
                </div>
                <div className="patient-history-graphs-container">
                    {graphKeys.map(paramId => {
                        const paramTitle = idToKey(paramId);
                        
                        if (!paramTitle) return null;
                        const paramData = graphValues[paramId] || [];
                        const chartPoints = mapPatientHistoryRowsToChartPoints(paramData);
                        const validYValues = chartPoints
                            .map(p => p.y)
                            .filter(y => typeof y === "number");
                        const maxY = validYValues.length ? Math.max(...validYValues) : 0;
                        const padding = Math.max(maxY * 0.1, 1);
                        const yAxisMax = maxY + padding;
                        const stepSize = yAxisMax / 4;

                        return (
                            <div className="graph-container" key={paramId}>
                                <div className="graph-title" ref={el => graphTitleRefs.current[paramId] = el}>
                                    {formatParamTitle(paramId)} : --
                                </div>
                                <Line
                                    ref={(el) => { graphRefs.current[paramId] = el; }}
                                    className="line-graph-vital"
                                    data={{
                                        datasets: [
                                            {
                                                label: paramTitle,
                                                data: chartPoints,
                                                borderWidth: 2,
                                                borderColor: "#FFFFFF",
                                                fill: false,
                                                tension: 0,
                                                pointRadius: 0,
                                                pointHoverRadius: 0,
                                                pointHitRadius: 0,
                                                spanGaps: false,
                                            },
                                        ],
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        animation: {
                                            onComplete: () => {
                                                updateGraphBounds();
                                            }
                                        },
                                        onResize: () => {
                                            updateGraphBounds();
                                        },
                                        plugins: {
                                            legend: { display: false },
                                            title: {
                                                display: true,
                                                align: "start",
                                                color: "#FFFFFF",
                                                font: { size: 16, weight: 600 },
                                                padding: { bottom: 18 },
                                            },
                                            tooltip: {
                                                enabled: false,
                                            }
                                        },
                                        scales: {
                                            x: {
                                                type: "time",
                                                min: props?.PatientHistoryTimeData?.startDateTime,
                                                max: props?.PatientHistoryTimeData?.endDateTime,
                                                adapters: {
                                                    date: { zone: "Asia/Kolkata" }
                                                },
                                                time: {
                                                    displayFormats: {
                                                        second: "HH:mm:ss",
                                                    },
                                                },
                                                ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
                                                grid: { borderColor: "#4D4D4D" },
                                            },
                                            y: {
                                                min: 0,
                                                max: yAxisMax,
                                                position: "left",
                                                ticks: {
                                                    stepSize: stepSize,
                                                    callback: value => Number(value).toFixed(2),
                                                },
                                                afterFit(scale) {
                                                    scale.width = 60;
                                                },
                                                grid: {
                                                    borderColor: "#4D4D4D",
                                                    color: (ctx) =>
                                                        ctx.index % 4 === 0 ? "#808080" : "transparent",
                                                    borderDash: [4, 4],
                                                },
                                            }
                                        },
                                    }}
                                    style={{ height: "10vw" }}
                                />
                            </div>
                        );
                    })}

                    {hasMore &&
                        <div className="spinner-container">
                            <div className="spinner"></div>
                        </div>
                    }
                    <div style={{ height: "50px" }} />
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state, ownProps) => {
    const volatile = state.volatileDataReducer;
    const bedId = ownProps.bedId;
    return {
        PatientHistoryTimeData: volatile?.nx5PatientHistoryTimeData?.[bedId],
        PatientHistoryGraphData: volatile?.nx5PatientHistoryGraphApiData?.[bedId],
        PatientHistoryCursorData: volatile?.nx5PatientHistoryCursorData?.[bedId],
    };
};

const mapDispatchToProps = (dispatch) => ({
    nx5PatientHistoryGraphApiData: (payload) => dispatch(nx5PatientHistoryGraphApiData(payload)),
    nx5PatientHistoryCursorData: (payload) => dispatch(nx5PatientHistoryCursorData(payload)),
    nx5PatientHistoryCurrentCursorTime: (payload) => dispatch(nx5PatientHistoryCurrentCursorTime(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ParameterGraph);
